/**
 * livePeople — une personne de la salle, quel que soit le nombre de ses appareils.
 *
 * Chaque appareil connecté est un participant LiveKit distinct : le premier jeton reçoit l'id
 * d'utilisateur pour identité, chaque autre `<id>.<sha256 du jeton>` (le backend,
 * `LiveSession::getAuthDeviceHash`). Le même tuteur sur son ordinateur et son téléphone fait donc deux
 * participants au même id. Ce module les regroupe par personne (Wajih, 20/10) :
 *  - **la caméra** est allumée si un appareil filme, et chaque appareil qui filme garde sa vidéo :
 *    `cameras` les range, celle à montrer en grand d'abord — l'appareil dont le micro est ouvert ;
 *  - **ce choix est stable** (Wajih, 20/10) : il ne change que quand l'état des micros ou des caméras
 *    change, jamais sur la voix (le grand sautait d'un appareil à l'autre à chaque prise de parole).
 *    Deux micros ouverts, ou aucun : on garde le grand d'avant (`previous`), sinon celui qui filmait
 *    le premier, sinon le premier appareil (l'identité sans suffixe), pour que l'ordre ne dépende pas
 *    de l'arrivée ;
 *  - **le micro** n'est coupé que si tous ses micros le sont ;
 *  - **la présence** : un appareil quelconque.
 *
 * Et les miens (`myOtherDevices`, Wajih, daily du 21/10) : connecté ailleurs aussi, je dois le voir.
 *
 * Pur, sans rien de propre à l'élève : la salle commune, que le live du tuteur pourra reprendre.
 */

/** Ce qu'un appareil dit de lui : son identité LiveKit, sa caméra, son micro. */
export type LiveDevice = {
    /** L'id d'utilisateur (le préfixe de l'identité). */
    id: string
    identity: string
    isLocal: boolean
    cameraOn: boolean
    micOn: boolean
}

export type LivePerson = {
    id: string
    isLocal: boolean
    /** Ses appareils connectés, dans l'ordre de la salle. */
    identities: string[]
    /** Un de ses appareils filme. */
    cameraOn: boolean
    /** Un de ses micros est ouvert : coupé seulement si tous le sont. */
    micOn: boolean
    /** Les appareils qui filment, celui à montrer en grand d'abord. Vide : aucune caméra. */
    cameras: string[]
}

/** L'appareil à montrer en grand d'abord : micro ouvert. La voix n'y entre pas. */
function micRank(device: LiveDevice): number {
    return device.micOn ? 0 : 1
}

/** Le premier appareil d'une personne porte son id seul ; les suivants `id.<hash>`. */
function deviceOrder(a: LiveDevice, b: LiveDevice): number {
    const aMain = a.identity === a.id ? 0 : 1
    const bMain = b.identity === b.id ? 0 : 1
    if (aMain !== bMain) return aMain - bMain
    return a.identity < b.identity ? -1 : a.identity > b.identity ? 1 : 0
}

/**
 * Mes autres appareils : ceux de la salle qui portent l'id de l'appareil où je suis, sans être lui, dans
 * l'ordre de la salle (Wajih, daily du 21/10 : on reste connecté dans un navigateur sans s'en rendre
 * compte, et il faut le voir). Avant d'être dans la salle, aucun.
 */
export function myOtherDevices<T extends LiveDevice>(devices: readonly T[]): T[] {
    const here = devices.find((device) => device.isLocal)
    if (here == null) return []
    return devices.filter((device) => !device.isLocal && device.id === here.id)
}

const personKey = (person: { id: string; isLocal: boolean }) => `${person.isLocal ? "local" : "remote"}:${person.id}`

/**
 * Les personnes de la salle, dans l'ordre où leur premier appareil y paraît. `previous` : le résultat
 * d'avant, dont l'ordre des caméras départage ce que l'état des micros ne tranche pas.
 */
export function livePeople(devices: readonly LiveDevice[], previous: readonly LivePerson[] = []): LivePerson[] {
    const before = new Map(previous.map((person) => [personKey(person), person.cameras]))
    const byPerson = new Map<string, LiveDevice[]>()
    for (const device of devices) {
        const key = personKey(device)
        const list = byPerson.get(key)
        if (list != null) list.push(device)
        else byPerson.set(key, [device])
    }
    return [...byPerson.entries()].map(([key, list]) => {
        const earlier = before.get(key) ?? []
        // Une caméra déjà montrée garde son rang ; une nouvelle vient après.
        const seniority = (device: LiveDevice) => {
            const index = earlier.indexOf(device.identity)
            return index < 0 ? earlier.length : index
        }
        const filming = list
            .filter((device) => device.cameraOn)
            .sort((a, b) => micRank(a) - micRank(b) || seniority(a) - seniority(b) || deviceOrder(a, b))
        return {
            id: list[0].id,
            isLocal: list[0].isLocal,
            identities: list.map((device) => device.identity),
            cameraOn: filming.length > 0,
            micOn: list.some((device) => device.micOn),
            cameras: filming.map((device) => device.identity),
        }
    })
}
